// Driver creation form with multi-step validation
import { useForm } from 'react-hook-form';
import { useEffect, useMemo, useState } from 'react';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const FIELD_LABELS = {
  driverId: 'Driver ID',
  employeeId: 'Employee ID',
  firstName: 'First name',
  lastName: 'Last name',
  dateOfBirth: 'Date of birth',
  hireDate: 'Hire date',
  phone: 'Phone number',
  email: 'Email address',
  address: 'Street address',
  city: 'City',
  state: 'State',
  zipCode: 'ZIP code',
  cdlNumber: 'CDL number',
  cdlClass: 'CDL class',
  cdlIssueDate: 'CDL issue date',
  cdlExpirationDate: 'CDL expiration date',
  licenseState: 'License state',
  emergencyContact1Name: 'Primary emergency contact full name',
  emergencyContact1Relationship: 'Primary emergency contact relationship',
  emergencyContact1Phone: 'Primary emergency contact phone number',
  emergencyContact1Address: 'Primary emergency contact address',
  emergencyContact2Name: 'Secondary emergency contact full name',
  emergencyContact2Relationship: 'Secondary emergency contact relationship',
  emergencyContact2Phone: 'Secondary emergency contact phone number',
  emergencyContact2Address: 'Secondary emergency contact address',
  companyId: 'Company ID'
};

const ACRONYM_TOKENS = new Set(['CDL', 'DOT', 'ID', 'VIN']);

// Fields validated before leaving each step; mirrors the backend's required driver fields.
const SECTION_FIELDS = {
  personal: ['status', 'firstName', 'lastName', 'dateOfBirth', 'hireDate', 'phone', 'email', 'address', 'city', 'state', 'zipCode'],
  cdl: ['cdlNumber', 'cdlClass', 'cdlIssueDate', 'cdlExpirationDate', 'licenseState'],
  emergency: [
    'emergencyContact1Name',
    'emergencyContact1Relationship',
    'emergencyContact1Phone',
    'emergencyContact2Phone'
  ]
};

// Same normalization as driverService before sending; the backend accepts 1-20 letters/digits.
const CDL_NUMBER_PATTERN = /^[A-Z0-9]{1,20}$/;
const normalizeCdlNumber = (value) => (value || '').replace(/[\s-]/g, '').toUpperCase();

const startOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

// <input type="date"> values are YYYY-MM-DD; parse them as local dates for comparisons.
const parseDateInput = (value) => {
  if (!value) {
    return null;
  }
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const extractDigits = (value) => {
  if (!value || typeof value !== 'string') {
    return '';
  }
  return value.replace(/\D/g, '');
};

const isValidPhoneNumber = (value) => {
  if (!value || typeof value !== 'string') {
    return false;
  }

  const digits = extractDigits(value);
  if (digits.length < 10 || digits.length > 15) {
    return false;
  }

  return true;
};

const toDateInstance = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (typeof value.toDate === 'function') {
    const parsed = value.toDate();
    return parsed instanceof Date && !Number.isNaN(parsed.getTime()) ? parsed : null;
  }

  if (typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000);
  }

  if (typeof value._seconds === 'number') {
    return new Date(value._seconds * 1000);
  }

  return null;
};

const formatDateForInput = (value) => {
  const date = toDateInstance(value);
  if (!date) {
    return '';
  }

  return date.toISOString().slice(0, 10);
};

const mapBackendFieldToFormField = (field) => {
  if (!field || typeof field !== 'string') {
    return field;
  }

  if (field.startsWith('emergencyContact1.')) {
    const property = field.split('.')[1] || '';
    return `emergencyContact1${property.charAt(0).toUpperCase()}${property.slice(1)}`;
  }

  if (field.startsWith('emergencyContact2.')) {
    const property = field.split('.')[1] || '';
    return `emergencyContact2${property.charAt(0).toUpperCase()}${property.slice(1)}`;
  }

  return field;
};

const humanizeFieldKey = (key) => {
  if (!key || typeof key !== 'string') {
    return '';
  }

  if (FIELD_LABELS[key]) {
    return FIELD_LABELS[key];
  }

  const spaced = key
    .replace(/[._]/g, ' ')
    .replace(/([a-z])([A-Z0-9])/g, '$1 $2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
    .replace(/([0-9])([A-Za-z])/g, '$1 $2')
    .replace(/([A-Za-z])([0-9])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();

  if (!spaced) {
    return '';
  }

  return spaced
    .split(' ')
    .map((word) => {
      const upper = word.toUpperCase();
      if (FIELD_LABELS[word]) {
        return FIELD_LABELS[word];
      }
      if (ACRONYM_TOKENS.has(upper)) {
        return upper;
      }
      if (/^\d+$/.test(word)) {
        return word;
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

const escapeRegex = (value) => {
  if (typeof value !== 'string') {
    return '';
  }
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

const normalizeValidationMessage = (message, context = {}) => {
  if (!message || typeof message !== 'string') {
    return '';
  }

  let normalized = message.replace(/["']/g, '').replace(/\s+/g, ' ').trim();
  if (!normalized) {
    return '';
  }

  const tokens = new Set();
  const addToken = (token) => {
    if (!token || typeof token !== 'string') {
      return;
    }
    tokens.add(token);
    const mapped = mapBackendFieldToFormField(token);
    if (mapped && mapped !== token) {
      tokens.add(mapped);
    }
    if (token.includes('.')) {
      token.split('.').forEach(addToken);
    }
  };

  addToken(context.field);
  addToken(context.mappedField);

  tokens.forEach((token) => {
    const humanLabel = humanizeFieldKey(token);
    if (!humanLabel) {
      return;
    }
    const pattern = new RegExp(`\\b${escapeRegex(token)}\\b`, 'g');
    normalized = normalized.replace(pattern, humanLabel);
  });

  const leadingMatch = normalized.match(/^([A-Za-z][A-Za-z0-9_.]*)/);
  if (leadingMatch) {
    const humanLabel = humanizeFieldKey(leadingMatch[1]);
    if (humanLabel) {
      normalized = humanLabel + normalized.slice(leadingMatch[1].length);
    }
  }

  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const BASE_DEFAULT_VALUES = {
  // Personal Information
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  hireDate: '',
  phone: '',
  email: '',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  status: 'active',

  // CDL Information
  cdlNumber: '',
  cdlClass: 'Class A',
  cdlIssueDate: '',
  cdlExpirationDate: '',
  licenseState: '',
  hazmatEndorsement: false,
  passengerEndorsement: false,
  schoolBusEndorsement: false,
  doubleTripleEndorsement: false,

  // Emergency Contacts
  emergencyContact1Name: '',
  emergencyContact1Relationship: '',
  emergencyContact1Phone: '',
  emergencyContact1Address: '',

  emergencyContact2Name: '',
  emergencyContact2Relationship: '',
  emergencyContact2Phone: '',
  emergencyContact2Address: ''
};

const buildDefaultValues = (driver = null) => {
  if (!driver) {
    return { ...BASE_DEFAULT_VALUES };
  }

  return {
    ...BASE_DEFAULT_VALUES,
    ...driver,
    dateOfBirth: formatDateForInput(driver.dateOfBirth),
    hireDate: formatDateForInput(driver.hireDate || driver.originalHireDate),
    cdlIssueDate: formatDateForInput(driver.cdlIssueDate),
    cdlExpirationDate: formatDateForInput(driver.cdlExpirationDate),
    emergencyContact1Name: driver.emergencyContact1?.name || driver.emergencyContact1Name || '',
    emergencyContact1Relationship: driver.emergencyContact1?.relationship || driver.emergencyContact1Relationship || '',
    emergencyContact1Phone: driver.emergencyContact1?.phone || driver.emergencyContact1Phone || '',
    emergencyContact1Address: driver.emergencyContact1?.address || driver.emergencyContact1Address || '',
    emergencyContact2Name: driver.emergencyContact2?.name || driver.emergencyContact2Name || '',
    emergencyContact2Relationship: driver.emergencyContact2?.relationship || driver.emergencyContact2Relationship || '',
    emergencyContact2Phone: driver.emergencyContact2?.phone || driver.emergencyContact2Phone || '',
    emergencyContact2Address: driver.emergencyContact2?.address || driver.emergencyContact2Address || '',
    status: (driver.status || (driver.isActive ? 'active' : 'inactive') || 'active').toLowerCase()
  };
};

const DriverForm = ({ driver = null, onSubmit, onCancel = null, serverError = null, onNavigateToDocuments = null }) => {
  const [activeSection, setActiveSection] = useState('personal');
  const [formErrors, setFormErrors] = useState([]);
  const [driverCreated, setDriverCreated] = useState(false);
  const [createdDriverData, setCreatedDriverData] = useState(null);
  const defaultValues = useMemo(() => buildDefaultValues(driver), [driver]);
  const formFieldNames = useMemo(() => new Set(Object.keys(defaultValues)), [defaultValues]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    clearErrors,
    setError,
    reset,
    setFocus,
    setValue,
    trigger,
    getValues
  } = useForm({
    defaultValues,
    mode: 'onSubmit'
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    if (Array.isArray(serverError)) {
      setFormErrors(serverError.map((message) => normalizeValidationMessage(message)));
    } else if (typeof serverError === 'string' && serverError.trim()) {
      setFormErrors([normalizeValidationMessage(serverError)]);
    } else if (!serverError) {
      setFormErrors([]);
    }
  }, [serverError]);

  const sections = driver ? [
    { id: 'personal', name: 'Personal Info', icon: '👤' },
    { id: 'cdl', name: 'CDL Information', icon: '🚛' },
    { id: 'emergency', name: 'Emergency Contacts', icon: '📞' }
  ] : [
    { id: 'personal', name: 'Personal Info', icon: '👤' },
    { id: 'cdl', name: 'CDL Information', icon: '🚛' },
    { id: 'emergency', name: 'Emergency Contacts', icon: '📞' },
    { id: 'documents', name: 'Upload Documents (Optional)', icon: '📄' }
  ];

  const cdlClasses = ['Class A', 'Class B', 'Class C'];
  const relationships = ['Spouse', 'Parent', 'Child', 'Sibling', 'Friend', 'Other'];
  const statusValue = (watch('status') || 'active').toLowerCase();
  const statusError = errors.status?.message;

  const setStatusValue = (value) => {
    const normalized = value.toLowerCase();
    setValue('status', normalized, { shouldDirty: true, shouldValidate: true });
    clearErrors('status');
  };

  const onFormSubmit = async (data) => {
    try {
      setFormErrors([]);
      const result = await onSubmit(data);

      // If creating a new driver (not editing), show success and move to documents tab
      if (!driver && result) {
        setCreatedDriverData(result);
        setDriverCreated(true);
        setActiveSection('documents');
      }
    } catch (error) {
      console.error('Form submission error:', error);

      const validationDetails = Array.isArray(error?.details)
        ? error.details
        : [];

      const normalizedMessages = [];
      let firstInvalidField = null;

      if (validationDetails.length) {
        validationDetails.forEach((detail) => {
          const mappedField = mapBackendFieldToFormField(detail?.field);
          const message = normalizeValidationMessage(detail?.message, {
            field: detail?.field,
            mappedField
          });

          if (mappedField && formFieldNames.has(mappedField)) {
            setError(mappedField, { type: 'server', message });
            if (!firstInvalidField) {
              firstInvalidField = mappedField;
            }
          }

          if (message) {
            normalizedMessages.push(message);
          }
        });
      }

      if (!validationDetails.length) {
        const fallbackMessages = Array.isArray(error?.validationMessages)
          ? error.validationMessages
          : [error?.message].filter(Boolean);

        fallbackMessages
          .map((message) => normalizeValidationMessage(message))
          .filter(Boolean)
          .forEach((message) => normalizedMessages.push(message));
      }

      const uniqueMessages = [...new Set(normalizedMessages.filter(Boolean))];

      if (uniqueMessages.length) {
        setFormErrors(uniqueMessages);
      } else if (serverError) {
        if (Array.isArray(serverError)) {
          setFormErrors(serverError.map((message) => normalizeValidationMessage(message)));
        } else if (typeof serverError === 'string') {
          setFormErrors([normalizeValidationMessage(serverError)]);
        }
      }

      if (firstInvalidField) {
        const targetSection = findSectionForField(firstInvalidField);
        if (targetSection) {
          setActiveSection(targetSection);
        }
        setTimeout(() => setFocus(firstInvalidField), 0);
      }
    }
  };

  const findSectionForField = (field) => (
    Object.keys(SECTION_FIELDS).find((sectionId) => SECTION_FIELDS[sectionId].includes(field)) || null
  );

  // Every step stays mounted, so submit validates all fields; show the first step with a problem.
  const onInvalidSubmit = (invalidFields) => {
    const targetSection = Object.keys(SECTION_FIELDS).find((sectionId) => (
      SECTION_FIELDS[sectionId].some((field) => invalidFields[field])
    ));

    if (targetSection) {
      setActiveSection(targetSection);
      setFormErrors(targetSection === activeSection
        ? []
        : [`Please fix the highlighted fields in ${sections.find((section) => section.id === targetSection)?.name}.`]);
    }
  };

  // Runs the field rules registered below for one step. All steps stay mounted,
  // so this works for any step, not only the visible one.
  const validateSection = async (sectionId) => {
    const fields = SECTION_FIELDS[sectionId];
    if (!fields) {
      return true;
    }
    return trigger(fields);
  };

  const goToPreviousSection = () => {
    const currentIndex = sections.findIndex(section => section.id === activeSection);
    if (currentIndex > 0) {
      setFormErrors([]);
      setActiveSection(sections[currentIndex - 1].id);
    }
  };

  const goToNextSection = async () => {
    const currentIndex = sections.findIndex(section => section.id === activeSection);
    const currentSection = sections[currentIndex]?.id;

    console.log('📄 DRIVER-FORM: Next button clicked', {
      currentSection,
      formValues: watch(),
      errors: Object.keys(errors || {})
    });

    if (!(await validateSection(currentSection))) {
      console.log("There is an in valid section: ", currentSection)
      return;
    }

    setFormErrors([]);

    if (currentIndex < sections.length - 1) {
      setActiveSection(sections[currentIndex + 1].id);
    }
  };

  const isFirstSection = activeSection === sections[0].id;
  const isLastSection = activeSection === sections[sections.length - 1].id;

  const renderPersonalInfo = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-sm font-semibold text-blue-900">Driver Status</h3>
            <p className="mt-1 text-sm text-blue-700">Toggle whether this driver is active and available for assignments.</p>
          </div>
          <div className="mt-3 sm:mt-0 inline-flex rounded-md shadow-sm">
            <button
              type="button"
              onClick={() => setStatusValue('active')}
              className={`px-4 py-2 text-sm font-medium border ${
                statusValue !== 'inactive'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-blue-600 border-blue-200 hover:border-blue-400'
              } rounded-l-md`}
            >
              Active
            </button>
            <button
              type="button"
              onClick={() => setStatusValue('inactive')}
              className={`px-4 py-2 text-sm font-medium border ${
                statusValue === 'inactive'
                  ? 'bg-red-600 text-white border-red-600'
                  : 'bg-white text-red-600 border-red-200 hover:border-red-400'
              } rounded-r-md`}
            >
              Inactive
            </button>
          </div>
        </div>
        <input type="hidden" {...register('status', { required: 'Status is required' })} />
        {statusError && (
          <p className="mt-3 text-sm text-red-600">{statusError}</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            First Name *
          </label>
          <input
            type="text"
            {...register('firstName', { 
              required: 'First name is required',
              minLength: { value: 2, message: 'First name must be at least 2 characters' }
            })}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.firstName ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.firstName && (
            <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Last Name *
          </label>
          <input
            type="text"
            {...register('lastName', { 
              required: 'Last name is required',
              minLength: { value: 2, message: 'Last name must be at least 2 characters' }
            })}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.lastName ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.lastName && (
            <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date of Birth *
          </label>
          <input
            type="date"
            {...register('dateOfBirth', { 
              required: 'Date of birth is required',
              validate: {
                minimumAge: (value) => {
                  const birthDate = new Date(value);
                  const today = new Date();
                  const age = today.getFullYear() - birthDate.getFullYear();
                  return age >= 18 || 'Must be at least 18 years old';
                },
                notBefore1920: (value) => parseDateInput(value) >= new Date(1920, 0, 1) || 'Date of birth cannot be before 1920'
              }
            })}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.dateOfBirth ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.dateOfBirth && (
            <p className="mt-1 text-sm text-red-600">{errors.dateOfBirth.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Hire Date *
          </label>
          <input
            type="date"
            {...register('hireDate', {
              required: 'Hire date is required',
              validate: {
                notFuture: (value) => {
                  if (!value) return 'Hire date is required';
                  const selected = new Date(value);
                  const today = new Date();
                  selected.setHours(0, 0, 0, 0);
                  today.setHours(0, 0, 0, 0);
                  return selected <= today || 'Hire date cannot be in the future';
                }
              }
            })}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.hireDate ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.hireDate && (
            <p className="mt-1 text-sm text-red-600">{errors.hireDate.message}</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:col-span-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number *
            </label>
          <input
            type="tel"
            {...register('phone', { 
              required: 'Phone number is required',
              validate: (value) => isValidPhoneNumber(value) || 'Please enter a valid phone number'
            })}
            placeholder="(555) 123-4567"
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.phone ? 'border-red-500' : 'border-gray-300'
            }`}
            />
            {errors.phone && (
              <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              {...register('email', { 
                required: 'Email address is required',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Please enter a valid email address'
                }
              })}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
            )}
          </div>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Street Address *
          </label>
          <input
            type="text"
            {...register('address', { required: 'Address is required' })}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.address ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.address && (
            <p className="mt-1 text-sm text-red-600">{errors.address.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            City *
          </label>
          <input
            type="text"
            {...register('city', { required: 'City is required' })}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.city ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.city && (
            <p className="mt-1 text-sm text-red-600">{errors.city.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            State *
          </label>
          <select
            {...register('state', { 
              required: 'State is required'
            })}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.state ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select State</option>
            {US_STATES.map(stateCode => (
              <option key={stateCode} value={stateCode}>{stateCode}</option>
            ))}
          </select>
          {errors.state && (
            <p className="mt-1 text-sm text-red-600">{errors.state.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            ZIP Code *
          </label>
          <input
            type="text"
            {...register('zipCode', { 
              required: 'ZIP code is required',
              pattern: {
                value: /^\d{5}(-\d{4})?$/,
                message: 'Please enter a valid ZIP code'
              }
            })}
            placeholder="12345 or 12345-6789"
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.zipCode ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.zipCode && (
            <p className="mt-1 text-sm text-red-600">{errors.zipCode.message}</p>
          )}
        </div>
      </div>
    </div>
  );

  const renderCDLInfo = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            CDL Number *
          </label>
          <input
            type="text"
            {...register('cdlNumber', {
              required: 'CDL number is required',
              validate: (value) => CDL_NUMBER_PATTERN.test(normalizeCdlNumber(value))
                || 'CDL number must be 1-20 letters or numbers (spaces and dashes are ignored)'
            })}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.cdlNumber ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.cdlNumber && (
            <p className="mt-1 text-sm text-red-600">{errors.cdlNumber.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            CDL Class *
          </label>
          <select
            {...register('cdlClass', { required: 'CDL class is required' })}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.cdlClass ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            {cdlClasses.map((cdlClass) => (
              <option key={cdlClass} value={cdlClass}>
                {cdlClass}
              </option>
            ))}
          </select>
          {errors.cdlClass && (
            <p className="mt-1 text-sm text-red-600">{errors.cdlClass.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            CDL Issue Date *
          </label>
          <input
            type="date"
            {...register('cdlIssueDate', {
              required: 'CDL issue date is required',
              validate: (value) => parseDateInput(value) <= startOfToday() || 'CDL issue date cannot be in the future'
            })}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.cdlIssueDate ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.cdlIssueDate && (
            <p className="mt-1 text-sm text-red-600">{errors.cdlIssueDate.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            CDL Expiration Date *
          </label>
          <input
            type="date"
            {...register('cdlExpirationDate', { 
              required: 'CDL expiration date is required',
              validate: {
                // Only new drivers need a current CDL; existing drivers with an expired CDL must stay editable.
                futureDate: (value) => Boolean(driver?.id) || parseDateInput(value) > startOfToday() || 'CDL expiration date must be in the future',
                afterIssueDate: (value) => {
                  const issueDate = parseDateInput(getValues('cdlIssueDate'));
                  return !issueDate || parseDateInput(value) > issueDate || 'CDL expiration date must be after issue date';
                }
              }
            })}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.cdlExpirationDate ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.cdlExpirationDate && (
            <p className="mt-1 text-sm text-red-600">{errors.cdlExpirationDate.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            License State *
          </label>
          <select
            {...register('licenseState', { required: 'License state is required' })}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.licenseState ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select State</option>
            {US_STATES.map(stateCode => (
              <option key={stateCode} value={stateCode}>{stateCode}</option>
            ))}
          </select>
          {errors.licenseState && (
            <p className="mt-1 text-sm text-red-600">{errors.licenseState.message}</p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-4">
          Endorsements
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="hazmat"
              {...register('hazmatEndorsement')}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="hazmat" className="ml-2 block text-sm text-gray-700">
              HAZMAT (Hazardous Materials)
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="passenger"
              {...register('passengerEndorsement')}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="passenger" className="ml-2 block text-sm text-gray-700">
              Passenger
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="schoolBus"
              {...register('schoolBusEndorsement')}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="schoolBus" className="ml-2 block text-sm text-gray-700">
              School Bus
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="doubleTriple"
              {...register('doubleTripleEndorsement')}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="doubleTriple" className="ml-2 block text-sm text-gray-700">
              Double/Triple Trailers
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDocumentsInfo = () => {
    if (!driverCreated || !createdDriverData) {
      return null;
    }

    const handleUploadLater = () => {
      if (onCancel) {
        onCancel();
      }
    };

    const handleStartUploading = () => {
      if (onNavigateToDocuments && createdDriverData) {
        onNavigateToDocuments(createdDriverData);
      }
    };

    return (
      <div className="space-y-6">
        {/* Success Message */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-semibold text-green-900">Driver Added Successfully!</h3>
              <p className="mt-2 text-sm text-green-700">
                {createdDriverData.firstName} {createdDriverData.lastName} has been added to your system.
              </p>
            </div>
          </div>
        </div>

        {/* Document Upload Encouragement */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h4 className="text-base font-semibold text-blue-900 mb-2">Upload Driver Documents</h4>
              <p className="text-sm text-blue-700 mb-4">
                Keep all important driver documents organized in one place. Upload documents now to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-sm text-blue-700 mb-6">
                <li>Get automatic expiration reminders for CDL, medical certificates, and other documents</li>
                <li>Ensure compliance with DOT regulations</li>
                <li>Access driver documents quickly when needed</li>
                <li>Maintain a complete digital record for each driver</li>
              </ul>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={handleStartUploading}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  Upload Now
                </button>
                <button
                  type="button"
                  onClick={handleUploadLater}
                  className="flex-1 px-6 py-3 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  Upload Later
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Document Types Information */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Recommended Documents to Upload:</h4>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
            <li className="flex items-center">
              <svg className="h-4 w-4 text-gray-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Commercial Driver's License (CDL)
            </li>
            <li className="flex items-center">
              <svg className="h-4 w-4 text-gray-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Medical Certificate
            </li>
            <li className="flex items-center">
              <svg className="h-4 w-4 text-gray-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Driver's Application
            </li>
            <li className="flex items-center">
              <svg className="h-4 w-4 text-gray-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              MVR (Motor Vehicle Record)
            </li>
            <li className="flex items-center">
              <svg className="h-4 w-4 text-gray-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Accident Record
            </li>
            <li className="flex items-center">
              <svg className="h-4 w-4 text-gray-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Drug & Alcohol Test Results
            </li>
          </ul>
        </div>
      </div>
    );
  };

  const renderEmergencyContacts = () => (
    <div className="space-y-8">
      {/* Emergency Contact 1 */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Primary Emergency Contact *</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Name *
            </label>
            <input
              type="text"
              {...register('emergencyContact1Name', { required: 'Emergency contact name is required' })}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.emergencyContact1Name ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.emergencyContact1Name && (
              <p className="mt-1 text-sm text-red-600">{errors.emergencyContact1Name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Relationship *
            </label>
            <select
              {...register('emergencyContact1Relationship', { required: 'Relationship is required' })}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.emergencyContact1Relationship ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select relationship</option>
              {relationships.map((relationship) => (
                <option key={relationship} value={relationship}>
                  {relationship}
                </option>
              ))}
            </select>
            {errors.emergencyContact1Relationship && (
              <p className="mt-1 text-sm text-red-600">{errors.emergencyContact1Relationship.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number *
            </label>
            <input
              type="tel"
              {...register('emergencyContact1Phone', {
                required: 'Emergency contact phone is required',
                validate: (value) => isValidPhoneNumber(value) || 'Please enter a valid phone number'
              })}
              placeholder="(555) 123-4567"
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.emergencyContact1Phone ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.emergencyContact1Phone && (
              <p className="mt-1 text-sm text-red-600">{errors.emergencyContact1Phone.message}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Address
            </label>
            <input
              type="text"
              {...register('emergencyContact1Address')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Emergency Contact 2 */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Secondary Emergency Contact</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Name
            </label>
            <input
              type="text"
              {...register('emergencyContact2Name')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Relationship
            </label>
            <select
              {...register('emergencyContact2Relationship')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select relationship</option>
              {relationships.map((relationship) => (
                <option key={relationship} value={relationship}>
                  {relationship}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              {...register('emergencyContact2Phone', {
                validate: (value) => !value || isValidPhoneNumber(value) || 'Please enter a valid phone number'
              })}
              placeholder="(555) 123-4567"
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.emergencyContact2Phone ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.emergencyContact2Phone && (
              <p className="mt-1 text-sm text-red-600">{errors.emergencyContact2Phone.message}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Address
            </label>
            <input
              type="text"
              {...register('emergencyContact2Address')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col">
      {/* Sticky Section Navigation */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-4 sm:space-x-8 overflow-x-auto scrollbar-hide">
            {sections.map((section) => (
              <button
                key={section.id}
                type="button"
                onClick={() => {
                  clearErrors();
                  setFormErrors([]);
                  setActiveSection(section.id);
                }}
                className={`flex items-center py-4 px-2 sm:px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeSection === section.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2 text-base sm:text-lg">{section.icon}</span>
                <span className="hidden sm:inline">{section.name}</span>
                <span className="sm:hidden text-xs">{section.name.split(' ')[0]}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Scrollable Form Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <form onSubmit={handleSubmit(onFormSubmit, onInvalidSubmit)}>
              {formErrors.length > 0 && (
                <div className="px-4 sm:px-6 pt-6">
                  <div className="mb-6 rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                    {formErrors.length === 1 ? (
                      <p>{formErrors[0]}</p>
                    ) : (
                      <ul className="list-disc space-y-1 pl-5">
                        {formErrors.map((message, index) => (
                          <li key={index}>{message}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}

              <div className="px-4 sm:px-6 py-6 sm:py-8">
                {/* Steps are hidden rather than unmounted: react-hook-form skips validation of unmounted fields */}
                <div className={activeSection === 'personal' ? '' : 'hidden'}>{renderPersonalInfo()}</div>
                <div className={activeSection === 'cdl' ? '' : 'hidden'}>{renderCDLInfo()}</div>
                <div className={activeSection === 'emergency' ? '' : 'hidden'}>{renderEmergencyContacts()}</div>
                {activeSection === 'documents' && renderDocumentsInfo()}
              </div>

              {/* Sticky Form Actions */}
              <div className="sticky bottom-0 px-4 sm:px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row sm:justify-between space-y-3 sm:space-y-0 sm:space-x-3 shadow-lg">
                {/* Previous and Next/Save buttons - appear first on mobile, second (right) on desktop */}
                <div className="flex space-x-3 order-1 sm:order-2">
                  {/* Hide navigation buttons on documents tab */}
                  {activeSection !== 'documents' && (
                    <>
                      <button
                        type="button"
                        onClick={goToPreviousSection}
                        disabled={isFirstSection}
                        className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Previous
                      </button>
                      {(() => {
                        // For editing existing driver
                        if (driver) {
                          if (isLastSection) {
                            return (
                              <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full sm:w-auto px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                {isSubmitting ? 'Saving...' : 'Update Driver'}
                              </button>
                            );
                          }
                          return (
                            <button
                              type="button"
                              onClick={goToNextSection}
                              className="w-full sm:w-auto px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                            >
                              Next
                            </button>
                          );
                        }

                        // For creating new driver - submit on emergency contacts tab
                        if (activeSection === 'emergency') {
                          return (
                            <button
                              type="submit"
                              disabled={isSubmitting}
                              className="w-full sm:w-auto px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {isSubmitting ? 'Creating Driver...' : 'Add Driver'}
                            </button>
                          );
                        }

                        return (
                          <button
                            type="button"
                            onClick={goToNextSection}
                            className="w-full sm:w-auto px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                          >
                            Next
                          </button>
                        );
                      })()}
                    </>
                  )}
                </div>
                {/* Cancel button - appears second on mobile, first (left) on desktop - hide on documents tab */}
                {activeSection !== 'documents' && (
                  <div className="flex space-x-3 order-2 sm:order-1">
                    {typeof onCancel === 'function' && (
                      <button
                        type="button"
                        onClick={onCancel}
                        className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverForm;
